import { createElement } from '../../../core/dom/helpers.js';
import { createOptionsProvider, subscribeWithInitial } from '../../../core/options/options-provider.js';
import { rebuildSelectOptions } from '../../../core/options/rebuild-select-options.js';

/** @type {import('../../../contracts/forms.js').FieldDriver} */
export const selectDriver = {
  type: 'select',
  mount(container, ctx) {
    const { def, onChange } = ctx;
    const selectDef = /** @type {import('../../../contracts/forms.js').SelectFieldDef} */ (def);
    const placeholder = def.placeholder ?? 'Select...';

    const select = /** @type {HTMLSelectElement} */ (createElement('select', {
      id: `field-${def.key}`,
      name: def.key,
      class: 'form-field-input',
    }));
    if (def.required) { select.setAttribute('required', 'required'); select.setAttribute('aria-required', 'true'); }
    if (def.disabled) select.setAttribute('disabled', 'disabled');
    container.appendChild(select);

    if (selectDef.loadOptions) {
      // --- Async path ---
      let destroyed = false;
      let currentValue = ctx.value != null ? String(ctx.value) : '';
      let initialRender = true;
      let forcedDisabled = false;
      /** @type {ReadonlyArray<import('../../../contracts/forms.js').FieldOption> | null} */
      let prevOptions = null;

      const provider = createOptionsProvider({
        options: selectDef.options,
        loadOptions: selectDef.loadOptions,
        mapOption: selectDef.mapOption,
      });

      const unsubscribe = subscribeWithInitial(provider, (state) => {
        if (destroyed) return;

        // Always update loading UI — preserves forcedDisabled state
        select.disabled = forcedDisabled || Boolean(def.disabled) || state.loading;

        // Only rebuild when options actually changed
        if (state.options !== prevOptions) {
          prevOptions = state.options;

          if (initialRender) {
            // Initial render: build options but don't fire onChange
            rebuildSelectOptions(select, state.options, placeholder, currentValue);
            initialRender = false;
          } else {
            const valueLost = rebuildSelectOptions(select, state.options, placeholder, currentValue);
            if (valueLost) {
              currentValue = '';
              onChange(currentValue);
            }
          }
        }
      });

      select.addEventListener('change', () => {
        currentValue = select.value;
        onChange(currentValue);
      });

      provider.load('');

      return {
        setValue(value) {
          currentValue = value == null ? '' : String(value);
          select.value = currentValue;
        },
        getValue() {
          return currentValue;
        },
        setErrors() {
          // errors rendered by host
        },
        destroy() {
          destroyed = true;
          unsubscribe();
          provider.destroy();
          select.remove();
        },
        setDisabled(disabled) {
          forcedDisabled = disabled;
          select.disabled = forcedDisabled || Boolean(def.disabled) || provider.getState().loading;
        },
        clearValue() {
          currentValue = '';
          select.value = '';
        },
        getOptionsProvider() {
          return provider;
        },
        setAriaInvalid(invalid) {
          select.setAttribute('aria-invalid', invalid ? 'true' : 'false');
        },
      };
    }

    // --- Static path (backward compatible) ---
    select.appendChild(createElement('option', { value: '' }, [placeholder]));
    for (const opt of selectDef.options ?? []) {
      select.appendChild(createElement('option', { value: opt.value }, [opt.label]));
    }
    select.addEventListener('change', () => onChange(select.value));

    if (ctx.value !== undefined) {
      select.value = ctx.value == null ? '' : String(ctx.value);
    }

    return {
      setValue(value) {
        select.value = value == null ? '' : String(value);
      },
      getValue() {
        return select.value;
      },
      setErrors() {
        // errors rendered by host
      },
      destroy() {
        select.remove();
      },
      setDisabled(disabled) {
        select.disabled = disabled;
      },
      clearValue() {
        select.value = '';
      },
      setAriaInvalid(invalid) {
        select.setAttribute('aria-invalid', invalid ? 'true' : 'false');
      },
    };
  },
};
